<template>
  <div class="view-course-page">
    <div v-if="loading" class="loading-state">
      <span class="spinner" aria-hidden="true" />
      <p>Loading course information…</p>
    </div>

    <div v-else-if="error" class="error-state">
      <h2 class="error-title">Course Not Found</h2>
      <p class="error-text">{{ error }}</p>
      <button type="button" @click="goHome" class="btn btn-primary">Return to Home</button>
    </div>

    <div v-else-if="course" class="course-details">
      <!-- Compact header -->
      <header class="course-header">
        <button type="button" @click="goBack" class="back-button" aria-label="Go back">← Back</button>
        <div class="course-header-inner">
          <div class="course-header-left">
            <h1 class="course-title">{{ course.name }}</h1>
            <div class="course-meta">
            <div v-if="course.reviewCount > 0" class="course-rating-inline">
              <StarRating :model-value="Math.round(course.averageOverallValue)" :interactive="false" />
              <span class="rating-value">{{ course.averageOverallValue.toFixed(1) }}</span>
              <span class="review-count">({{ course.reviewCount }} review{{ course.reviewCount !== 1 ? 's' : '' }})</span>
            </div>
          </div>
          </div>
          <div class="course-header-highlights">
            <span class="header-highlight header-price">
              <span class="header-highlight-label">Price</span>
              <span class="header-highlight-value">
                <template v-if="course.costType === 'free'">Free</template>
                <template v-else>${{ formatCost(course.cost) }}</template>
              </span>
            </span>
            <span class="header-highlight header-ceus">
              <span class="header-highlight-label">CEU's</span>
              <span class="header-highlight-value">{{ course.credits }}</span>
            </span>
          </div>
        </div>
      </header>

      <div class="course-content">
        <main class="main-info">
          <!-- Single Details band: Provider + Course + Schedule -->
          <section class="section-band" aria-labelledby="details-heading">
            <h2 id="details-heading" class="section-title">Details</h2>
            <div class="details-grid">
              <div class="detail-item">
                <span class="detail-label">Provider</span>
                <span class="detail-value">{{ course.providerName }}</span>
              </div>
              <div v-if="course.instructor" class="detail-item">
                <span class="detail-label">Instructor</span>
                <span class="detail-value">{{ course.instructor }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">Format</span>
                <span class="detail-value">{{ formatCourseType(course.courseType) }}</span>
              </div>
              <div v-if="course.dateTime" class="detail-item">
                <span class="detail-label">Date & time</span>
                <span class="detail-value">{{ formatDateTime(course.dateTime) }}</span>
              </div>
              <div v-if="course.location" class="detail-item">
                <span class="detail-label">Location</span>
                <span class="detail-value">{{ course.location }}</span>
              </div>
              <div v-if="course.expirationDate" class="detail-item">
                <span class="detail-label">Expiration date</span>
                <span class="detail-value">{{ formatDateOnly(course.expirationDate) }}</span>
              </div>
            </div>
          </section>

          <!-- Description -->
          <section v-if="course.description" class="section-band" aria-labelledby="description-heading">
            <h2 id="description-heading" class="section-title">Description</h2>
            <p class="description-text">{{ course.description }}</p>
          </section>

          <!-- Website -->
          <section v-if="course.website" class="section-band" aria-labelledby="website-heading">
            <h2 id="website-heading" class="section-title">More information</h2>
            <a :href="course.website" target="_blank" rel="noopener noreferrer" class="website-link">
              Visit course website →
            </a>
          </section>

          <!-- Review Summary (compact) -->
          <section v-if="course.reviewCount > 0" class="section-band review-summary-band" aria-labelledby="review-summary-heading">
            <h2 id="review-summary-heading" class="section-title">Review summary</h2>
            <div class="review-summary-inline">
              <div class="summary-row">
                <span class="summary-label">Overall</span>
                <div class="summary-rating">
                  <StarRating :model-value="Math.round(course.averageOverallValue)" :interactive="false" />
                  <span class="rating-number">{{ course.averageOverallValue.toFixed(1) }}</span>
                </div>
              </div>
              <div v-if="course.averageEducationalContent" class="summary-row">
                <span class="summary-label">Educational content</span>
                <div class="summary-rating">
                  <StarRating :model-value="Math.round(course.averageEducationalContent)" :interactive="false" />
                  <span class="rating-number">{{ course.averageEducationalContent.toFixed(1) }}</span>
                </div>
              </div>
              <div v-if="course.averageEngagement" class="summary-row">
                <span class="summary-label">Engagement</span>
                <div class="summary-rating">
                  <StarRating :model-value="Math.round(course.averageEngagement)" :interactive="false" />
                  <span class="rating-number">{{ course.averageEngagement.toFixed(1) }}</span>
                </div>
              </div>
              <span class="summary-meta">{{ course.reviewCount }} review{{ course.reviewCount !== 1 ? 's' : '' }}{{ course.lastReviewDate ? ' · Last ' + formatDate(course.lastReviewDate) : '' }}</span>
            </div>
          </section>

          <!-- Reviews Section -->
          <section class="section-band reviews-section" aria-labelledby="reviews-heading">
            <div class="reviews-header">
              <h2 id="reviews-heading" class="section-title">Reviews</h2>
              <button
                v-if="isAuthenticated"
                type="button"
                class="add-review-button"
                @click="showAddReviewModal = true"
              >
                Write a review
              </button>
            </div>

            <!-- Not Authenticated Overlay -->
            <div v-if="!isAuthenticated" class="reviews-overlay">
              <div class="overlay-content">
                <p v-if="hasReviews()">Log in or register to see {{ course.reviewCount || 0 }} review{{ (course.reviewCount || 0) !== 1 ? 's' : '' }} of this course.</p>
                <p v-else>Log in or register to review this course.</p>
              </div>
            </div>

            <!-- Authenticated Reviews -->
            <template v-else>
              <div v-if="reviewsLoading" class="reviews-loading">
                <span class="spinner spinner-small" aria-hidden="true" />
                <p>Loading reviews…</p>
              </div>

              <div v-else-if="reviews.length === 0" class="no-reviews">
                <p>No reviews yet. Be the first to review this course!</p>
              </div>

              <div v-else>
                <div class="reviews-list">
                  <div
                    v-for="review in reviews"
                    :key="review.id"
                    class="review-card"
                  >
                    <div class="review-header">
                      <div class="review-ratings">
                        <div class="review-rating-item">
                          <span class="rating-label">Educational Content:</span>
                          <StarRating :model-value="review.educationalContent" :interactive="false" />
                        </div>
                        <div class="review-rating-item">
                          <span class="rating-label">Engagement:</span>
                          <StarRating :model-value="review.engagement" :interactive="false" />
                        </div>
                        <div class="review-rating-item">
                          <span class="rating-label">Overall Value:</span>
                          <StarRating :model-value="review.overallValue" :interactive="false" />
                        </div>
                      </div>
                      <div class="review-header-right">
                        <div v-if="review.createdAt" class="review-date">
                          {{ formatDate(review.createdAt) }}
                        </div>
                        <button
                          v-if="isReviewAuthor(review)"
                          type="button"
                          class="edit-review-button"
                          aria-label="Edit review"
                          @click="handleEditReview(review)"
                        >
                          Edit
                        </button>
                      </div>
                    </div>
                    <div v-if="review.user" class="review-author">
                      <span class="author-name">{{ review.user.firstName }} {{ review.user.lastInitial }}.</span>
                      <span v-if="review.user.experienceLevel" class="author-experience">{{ review.user.experienceLevel }}</span>
                    </div>
                    <div class="review-text">
                      {{ review.reviewText }}
                    </div>
                  </div>
                </div>

                <!-- Reviews Pagination -->
                <div v-if="reviewsPagination.total_pages > 1" class="reviews-pagination">
                  <button
                    type="button"
                    class="pagination-btn"
                    :disabled="reviewsPage === 1"
                    @click="goToReviewsPage(reviewsPage - 1)"
                  >
                    ← Previous
                  </button>
                  <span class="pagination-info">
                    Page {{ reviewsPage }} of {{ reviewsPagination.total_pages }} ({{ reviewsPagination.total }} total)
                  </span>
                  <button
                    type="button"
                    class="pagination-btn"
                    :disabled="reviewsPage === reviewsPagination.total_pages"
                    @click="goToReviewsPage(reviewsPage + 1)"
                  >
                    Next →
                  </button>
                </div>
              </div>
            </template>
          </section>
        </main>
      </div>
    </div>

    <!-- Add Review Modal -->
    <AddReviewModal
      :show="showAddReviewModal"
      :course-id="courseId"
      :review-id="editingReviewId"
      :existing-review="editingReview"
      @close="handleCloseReviewModal"
      @review-submitted="handleReviewSubmitted"
    />
  </div>
</template>

<script>
import { getCourseById } from '../../services/courseService'
import { formatCourseType as formatCourseTypeModel } from '../../models/Course.js'
import { getReviewsByCourseId } from '../../services/reviewService.js'
import { isAuthenticated, getCurrentUser } from '../../services/authService.js'
import StarRating from '../shared/StarRating.vue'
import AddReviewModal from '../modals/AddReviewModal.vue'

export default {
  name: 'ViewCourse',
  props: {
    id: {
      type: String,
      required: true
    }
  },
  components: {
    StarRating,
    AddReviewModal
  },
  computed: {
    courseId() {
      return this.id
    },
    isAuthenticated() {
      return isAuthenticated()
    }
  },
  data() {
    return {
      course: null,
      loading: true,
      error: null,
      reviews: [],
      reviewsLoading: false,
      reviewsPagination: {
        page: 1,
        per_page: 20,
        total: 0,
        total_pages: 0
      },
      reviewsPage: 1,
      reviewsPerPage: 20,
      showAddReviewModal: false,
      editingReviewId: null,
      editingReview: null,
      currentUser: null
    }
  },
  async mounted() {
    // Load current user
    await this.loadCurrentUser()
    await this.loadCourse()
    // Only load reviews if user is authenticated
    if (this.courseId && this.isAuthenticated) {
      await this.loadReviews()
    }
  },
  beforeUnmount() {
    // Reset page title when leaving the page
    document.title = 'Dietedics'
  },
  watch: {
    courseId: {
      handler: async function(newId) {
        if (newId) {
          await this.loadCourse()
          // Only load reviews if user is authenticated
          if (this.isAuthenticated) {
            await this.loadReviews()
          }
        }
      },
      immediate: false
    }
  },
  methods: {
    async loadCurrentUser() {
      if (isAuthenticated()) {
        try {
          this.currentUser = await getCurrentUser()
        } catch (error) {
          console.error('Error loading current user:', error)
          this.currentUser = null
        }
      } else {
        this.currentUser = null
      }
    },
    isReviewAuthor(review) {
      return this.currentUser && review.userId === this.currentUser.id
    },
    hasReviews() {
      return this.course.reviewCount > 0
    },
    handleEditReview(review) {
      this.editingReviewId = review.id
      this.editingReview = review
      this.showAddReviewModal = true
    },
    handleCloseReviewModal() {
      this.showAddReviewModal = false
      this.editingReviewId = null
      this.editingReview = null
    },
    async loadCourse() {
      this.loading = true
      this.error = null
      
      try {
        const course = await getCourseById(this.courseId)
        if (course) {
          this.course = course
          // Update page title with course name
          document.title = `Dietedics - ${course.name}`
        } else {
          this.error = 'The course you are looking for could not be found.'
          document.title = 'Dietedics - Course Not Found'
        }
      } catch (err) {
        this.error = 'Failed to load course information.'
        console.error('Error loading course:', err)
        document.title = 'Dietedics - Error'
      } finally {
        this.loading = false
      }
    },
    async loadReviews() {
      this.reviewsLoading = true
      try {
        const result = await getReviewsByCourseId(this.courseId, {
          page: this.reviewsPage,
          perPage: this.reviewsPerPage
        })
        
        // Handle paginated response
        if (result.data && result.pagination) {
          this.reviews = result.data
          this.reviewsPagination = result.pagination
        } else {
          // Fallback for backward compatibility (array response)
          this.reviews = Array.isArray(result) ? result : []
          this.reviewsPagination = {
            page: 1,
            per_page: this.reviews.length,
            total: this.reviews.length,
            total_pages: 1
          }
        }
      } catch (err) {
        console.error('Error loading reviews:', err)
        this.reviews = []
        this.reviewsPagination = {
          page: 1,
          per_page: 0,
          total: 0,
          total_pages: 0
        }
      } finally {
        this.reviewsLoading = false
      }
    },
    goToReviewsPage(page) {
      if (page >= 1 && page <= this.reviewsPagination.total_pages && page !== this.reviewsPage) {
        this.reviewsPage = page
        this.loadReviews()
        // Scroll to reviews section
        this.$nextTick(() => {
          const reviewsSection = this.$el.querySelector('.reviews-section')
          if (reviewsSection) {
            reviewsSection.scrollIntoView({ behavior: 'smooth', block: 'start' })
          }
        })
      }
    },
    async handleReviewSubmitted() {
      await this.loadReviews()
    },
    formatCost(cost) {
      if (!cost) return '0.00'
      return parseFloat(cost).toFixed(2)
    },
    formatCourseType(type) {
      return formatCourseTypeModel(type)
    },
    formatDateTime(dateTime) {
      if (!dateTime) return ''
      try {
        const date = new Date(dateTime)
        return date.toLocaleString('en-US', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit'
        })
      } catch (e) {
        return dateTime
      }
    },
    formatDate(dateString) {
      if (!dateString) return ''
      try {
        const date = new Date(dateString)
        const now = new Date()
        const diffTime = Math.abs(now - date)
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
        
        if (diffDays === 0) return 'today'
        if (diffDays === 1) return 'yesterday'
        if (diffDays < 7) return `${diffDays} days ago`
        
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        })
      } catch (e) {
        return dateString
      }
    },
    formatDateOnly(dateString) {
      if (!dateString) return ''
      try {
        const date = new Date(dateString)
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        })
      } catch (e) {
        return dateString
      }
    },
    goBack() {
      this.$router.go(-1)
    },
    goHome() {
      this.$router.push({ name: 'landing' })
    }
  }
}
</script>

<style scoped>
/* Spacing scale: 8px base → 8, 16, 24, 32, 48 */
.view-course-page {
  max-width: 1200px;
  margin: 0 auto;
  padding: 48px 24px;
  min-height: 60vh;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 48px 24px;
  color: var(--color-text-secondary);
  font-size: 1rem;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid var(--color-border-main);
  border-top-color: var(--color-primary-main);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.spinner-small {
  width: 32px;
  height: 32px;
  border-width: 2px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.error-state {
  text-align: center;
  padding: 48px 24px;
}

.error-title {
  font-size: 1.5rem;
  color: var(--color-error-main);
  margin: 0 0 16px 0;
  font-weight: 600;
}

.error-text {
  color: var(--color-text-secondary);
  margin: 0 0 24px 0;
  font-size: 1rem;
}

.course-details {
  background: var(--color-surface-light);
  border: 1px solid var(--color-border-light);
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  overflow: hidden;
}

/* Compact header */
.course-header {
  background: linear-gradient(135deg, var(--color-primary-main) 0%, var(--color-primary-dark) 100%);
  color: var(--color-text-inverse);
  padding: 24px 32px;
  position: relative;
}

.back-button {
  background: rgba(255, 255, 255, 0.15);
  color: var(--color-text-inverse);
  border: 1px solid rgba(255, 255, 255, 0.25);
  padding: 8px 16px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.9375rem;
  font-weight: 600;
  margin-bottom: 16px;
  font-family: inherit;
  transition: background-color var(--transition-normal), border-color var(--transition-normal);
}

.back-button:hover {
  background: rgba(255, 255, 255, 0.25);
  border-color: rgba(255, 255, 255, 0.4);
}

.back-button:focus-visible {
  outline: 2px solid var(--color-text-inverse);
  outline-offset: 2px;
}

.course-header-inner {
  max-width: 100%;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
}

.course-header-left {
  flex: 1 1 auto;
  min-width: 0;
}

.course-title {
  font-size: 1.75rem;
  font-weight: 700;
  margin: 0 0 12px 0;
  line-height: 1.3;
}

.course-header-highlights {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 24px 32px;
  flex-shrink: 0;
  text-align: right;
}

.course-header-highlights .header-highlight {
  align-items: flex-end;
}

.header-highlight {
  display: inline-flex;
  flex-direction: column;
  gap: 2px;
}

.header-highlight-label {
  font-size: 0.875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  opacity: 0.9;
}

.header-highlight-value {
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 1.2;
  letter-spacing: 0.02em;
}

.header-price .header-highlight-value,
.header-ceus .header-highlight-value {
  font-size: 1.625rem;
}

.course-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px 16px;
}

.course-badges {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.badge {
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.8125rem;
  font-weight: 600;
  display: inline-block;
}

.badge-competency,
.badge-credits {
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(8px);
}

.badge-free {
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(8px);
}

.badge-cost {
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(8px);
}

.course-rating-inline {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.course-rating-inline .rating-value {
  font-size: 1rem;
  font-weight: 600;
}

.review-count {
  font-size: 0.9375rem;
  opacity: 0.95;
}

/* Main layout: full width */
.course-content {
  padding: 32px 24px;
}

.main-info {
  display: flex;
  flex-direction: column;
  gap: 24px;
  max-width: 100%;
}

/* Section bands (same pattern as Landing/Search) */
.section-band {
  background: var(--color-surface-light);
  border: 1px solid var(--color-border-light);
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}

.section-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--color-text-primary);
  margin: 0 0 16px 0;
}

/* Details grid: compact 2–3 column */
.details-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 12px 24px;
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 12px 16px;
  background: var(--color-bg-dark);
  border-radius: 8px;
  border: 1px solid var(--color-border-light);
}

.detail-label {
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--color-text-light);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.detail-value {
  font-size: 1.0625rem;
  font-weight: 600;
  color: var(--color-text-primary);
  line-height: 1.35;
}

.free-badge {
  background: var(--color-success-light);
  color: var(--color-success-dark);
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 0.8125rem;
  font-weight: 600;
  display: inline-block;
}

.description-text {
  color: var(--color-text-primary);
  line-height: 1.65;
  font-size: 1rem;
  margin: 0;
}

.website-link {
  display: inline-block;
  color: var(--color-primary-main);
  text-decoration: none;
  font-weight: 600;
  font-size: 1rem;
  transition: color var(--transition-normal);
}

.website-link:hover {
  color: var(--color-primary-dark);
  text-decoration: underline;
}

.website-link:focus-visible {
  outline: 2px solid var(--color-primary-main);
  outline-offset: 2px;
  border-radius: 4px;
}

/* Review summary: compact inline rows */
.review-summary-band .section-title {
  margin-bottom: 12px;
}

.review-summary-inline {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.summary-row {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.summary-row .summary-label {
  font-weight: 600;
  color: var(--color-text-secondary);
  font-size: 0.9375rem;
  min-width: 120px;
}

.summary-rating {
  display: flex;
  align-items: center;
  gap: 8px;
}

.rating-number {
  color: var(--color-text-primary);
  font-size: 0.9375rem;
  font-weight: 600;
}

.summary-meta {
  font-size: 0.875rem;
  color: var(--color-text-secondary);
  margin-top: 4px;
}

/* Reviews section */
.reviews-section .section-title {
  margin-bottom: 0;
}

.reviews-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;
}

.reviews-header .section-title {
  margin: 0;
}

.add-review-button {
  background-color: var(--color-primary-main);
  color: var(--color-text-inverse);
  border: 1px solid var(--color-primary-dark);
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 0.9375rem;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  transition: transform var(--transition-normal), box-shadow var(--transition-normal), background-color var(--transition-normal);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.add-review-button:hover {
  background-color: var(--color-primary-dark);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.add-review-button:focus-visible {
  outline: 2px solid var(--color-primary-dark);
  outline-offset: 2px;
}

.reviews-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 32px 24px;
  color: var(--color-text-secondary);
  font-size: 1rem;
}

.no-reviews {
  text-align: center;
  padding: 32px 24px;
  color: var(--color-text-secondary);
  font-size: 1rem;
}

.reviews-overlay {
  min-height: 120px;
  background: var(--color-bg-dark);
  border: 1px dashed var(--color-border-main);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 16px;
}

.overlay-content {
  text-align: center;
  padding: 24px 16px;
  font-size: 1rem;
  color: var(--color-text-secondary);
}

.overlay-content p {
  margin: 0;
}

.reviews-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.review-card {
  background: var(--color-surface-elevated);
  border: 1px solid var(--color-border-light);
  border-radius: 12px;
  padding: 20px;
  transition: box-shadow var(--transition-normal), border-color var(--transition-normal);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}

.review-card:hover {
  border-color: var(--color-border-main);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.review-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
  flex-wrap: wrap;
  gap: 12px;
}

.review-ratings {
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
}

.review-rating-item {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.rating-label {
  font-weight: 600;
  color: var(--color-text-secondary);
  font-size: 0.875rem;
  min-width: 120px;
}

.review-header-right {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
}

.review-date {
  color: var(--color-text-light);
  font-size: 0.8125rem;
  white-space: nowrap;
}

.edit-review-button {
  background: var(--color-primary-main);
  color: var(--color-text-inverse);
  border: 1px solid var(--color-primary-dark);
  border-radius: 6px;
  padding: 6px 14px;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  transition: background-color var(--transition-normal), transform var(--transition-normal);
}

.edit-review-button:hover {
  background: var(--color-primary-dark);
  transform: translateY(-1px);
}

.edit-review-button:focus-visible {
  outline: 2px solid var(--color-primary-dark);
  outline-offset: 2px;
}

.review-author {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid var(--color-border-light);
}

.author-name {
  font-weight: 600;
  color: var(--color-text-primary);
  font-size: 0.9375rem;
}

.author-experience {
  color: var(--color-text-secondary);
  font-size: 0.8125rem;
  padding: 2px 8px;
  background-color: var(--color-surface-dark);
  border-radius: 8px;
}

.review-text {
  color: var(--color-text-primary);
  line-height: 1.6;
  font-size: 0.9375rem;
  white-space: pre-wrap;
  word-wrap: break-word;
}

.reviews-pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 24px;
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid var(--color-border-light);
}

.reviews-pagination .pagination-btn {
  padding: 10px 20px;
  background-color: var(--color-primary-main);
  color: var(--color-text-inverse);
  border: 1px solid var(--color-primary-dark);
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.9375rem;
  font-family: inherit;
  transition: transform var(--transition-normal), background-color var(--transition-normal);
}

.reviews-pagination .pagination-btn:hover:not(:disabled) {
  background-color: var(--color-primary-dark);
  transform: translateY(-2px);
}

.reviews-pagination .pagination-btn:focus-visible {
  outline: 2px solid var(--color-primary-dark);
  outline-offset: 2px;
}

.reviews-pagination .pagination-btn:disabled {
  background-color: var(--color-border-main);
  border-color: var(--color-border-main);
  cursor: not-allowed;
  opacity: 0.6;
}

.reviews-pagination .pagination-info {
  color: var(--color-text-secondary);
  font-size: 0.9375rem;
}

/* Buttons */
.btn {
  padding: 12px 24px;
  font-size: 1rem;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  font-family: inherit;
  transition: transform var(--transition-normal), box-shadow var(--transition-normal), background-color var(--transition-normal), border-color var(--transition-normal);
}

.btn-primary {
  background-color: var(--color-primary-main);
  color: var(--color-text-inverse);
  border: 1px solid var(--color-primary-dark);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.btn-primary:hover {
  background-color: var(--color-primary-dark);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.btn-primary:focus-visible {
  outline: 2px solid var(--color-primary-dark);
  outline-offset: 2px;
}

@media (max-width: 768px) {
  .view-course-page {
    padding: 32px 16px;
  }

  .course-header {
    padding: 20px 24px;
  }

  .course-title {
    font-size: 1.5rem;
  }

  .course-header-highlights {
    gap: 20px 24px;
    margin-bottom: 10px;
  }

  .header-highlight-value {
    font-size: 1.375rem;
  }

  .header-price .header-highlight-value,
  .header-ceus .header-highlight-value {
    font-size: 1.5rem;
  }

  .course-content {
    padding: 24px 16px;
  }

  .section-band {
    padding: 20px 16px;
  }

  .details-grid {
    grid-template-columns: 1fr;
  }
}
</style>

